import {
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Tooltip,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import {
  getLabelsAsValues,
  getValueFromLabel,
  SelectOptionsType,
} from "../../../utils/select-helpers";
import { postMeetingRooms } from "../../../api/meeting-rooms/postMeetingRooms";
import { getMeetingRoomDls } from "../../../api/meeting-rooms/getMeetingRoomDls";
import { getSipNumbers_Filtered } from "../../../api/meeting-rooms/getSipNumbers_Filtered";
import { getMeetingRooms } from "../../../api/numbers/getMeetingRooms";
import HeaderContext from "../../../components/layout/header/header-context/HeaderContext";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";

interface Props {
  isVisible: boolean;
  handleClose: (refreshData?: boolean) => void;
}

interface State {
  isModalVisible: boolean;
  meetingRoomOptions?: SelectOptionsType[];
  meetingRoomDlOptions?: SelectOptionsType[];
  sipNumberOptions?: SelectOptionsType[];
  submitError?: string;
  assignTeamsProLicensesChecked: boolean;
}

const AddMeetingRoomFormModal: React.FC<Props> = ({
  isVisible,
  handleClose,
}) => {
  const [form] = Form.useForm();
  const { currentUserId } = useContext(HeaderContext);

  const [state, setState] = useState<State>({
    isModalVisible: false,
    meetingRoomOptions: [],
    meetingRoomDlOptions: [],
    sipNumberOptions: [],
    submitError: "",
    assignTeamsProLicensesChecked: false,
  });
  const {
    isModalVisible,
    meetingRoomOptions,
    meetingRoomDlOptions,
    sipNumberOptions,
    submitError,
    assignTeamsProLicensesChecked,
  } = state;

  const handleCancel = () => {
    form.resetFields();
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    handleClose();
  };

  const handleSubmit = () => {
    setState((prevState) => ({
      ...prevState,
      submitError: "",
    }));
    form
      .validateFields()
      .then((formFields) => {
        console.log("onOk: %o", formFields);
        const payload = {
          name: formFields.meetingRoomName,
          capacity: formFields.meetingRoomCapacity,
          clientId: currentUserId,
          meetingRoomDlId: Number(
            getValueFromLabel(formFields.meetingRoolDl, meetingRoomDlOptions)
          ),
          ...(assignTeamsProLicensesChecked
            ? {
                sipNumberId: Number(
                  getValueFromLabel(formFields.phoneNumber, sipNumberOptions)
                ),
                addLicense: true,
              }
            : {}),
        };
        console.log("Submitting postMeetingRooms: %o", payload);
        postMeetingRooms(payload).then((response) => {
          console.log("submitResponse:%o", response);
          if (response?.error) {
            setState((prevState) => ({
              ...prevState,
              submitError: response.error,
            }));
          } else {
            form.resetFields();
            setState((prevState) => ({
              ...prevState,
              submitError: "",
            }));
            handleClose(true);
          }
        });
      })
      .catch((err) => {
        console.log("onOk err: %o", err);
      });
  };

  useEffect(() => {
    if (isVisible !== isModalVisible) {
      setState((prevState) => ({
        ...prevState,
        isModalVisible: isVisible,
      }));

      if (!isVisible) return;

      getMeetingRooms().then((response) => {
        if (response.data) {
          console.log("meetingRoomsResponse: %o", response.data);
          setState((prevState) => ({
            ...prevState,
            meetingRoomOptions: response.data,
          }));
        }
      });

      getMeetingRoomDls().then((response) => {
        if (response.data) {
          console.log("meetingRoomDlsResponse: %o", response.data);
          setState((prevState) => ({
            ...prevState,
            meetingRoomDlOptions: response.data,
          }));
        }
      });

      getSipNumbers_Filtered().then((response) => {
        if (response.data) {
          console.log("sipNumbersResponse: %o", response.data);
          setState((prevState) => ({
            ...prevState,
            sipNumberOptions: response.data,
          }));
        }
      });
    }
  }, [isVisible, isModalVisible]);

  return (
    <Modal
      title="Add Meeting Room"
      maskClosable={false}
      open={isModalVisible}
      onCancel={handleCancel}
      cancelText="Cancel"
      okText="Submit"
      onOk={handleSubmit}
      okButtonProps={{
        className: "bdo-red-button-modal",
      }}
      cancelButtonProps={{
        className: "bdo-grey-button-modal",
      }}
    >
      <div className="my-4" />
      <Form form={form} labelCol={{ span: 24 }} wrapperCol={{ span: 24 }}>
        <Form.Item
          name="meetingRoolDl"
          label="Meeting Room DL:"
          rules={[{ required: true, message: "This field is required" }]}
        >
          <Select
            showSearch
            placeholder="Select a distribution list"
            onChange={() => {}}
            allowClear
            options={getLabelsAsValues(meetingRoomDlOptions)}
            filterOption={(inputValue, option) =>
              option!.value.toUpperCase().startsWith(inputValue.toUpperCase())
            }
          ></Select>
        </Form.Item>

        <Form.Item
          name="meetingRoomName"
          label="Meeting Room Name:"
          rules={[
            { required: true, message: "This field is required" },
            {
              validator: (_, value) => {
                if (
                  value &&
                  meetingRoomOptions?.some(
                    (option) =>
                      option.label.toLowerCase().trim() ===
                      value.toLowerCase().trim()
                  )
                ) {
                  return Promise.reject("Meeting room name already exists");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            placeholder="Enter a name"
            onChange={(e) => {
              //no spaces allowed, max length of 20 chas.
              const trimmedValue = e.target.value
                .replaceAll(/\s+/g, "")
                .slice(0, 20);
              form.setFieldsValue({ meetingRoomName: trimmedValue });
            }}
          />
        </Form.Item>

        <Form.Item className="flex mb-1" name="assignTeamsProLicense">
          <Checkbox
            onChange={(e) => {
              console.log("License: %o", e);
              setState((prevState) => ({
                ...prevState,
                assignTeamsProLicensesChecked: e.target.checked,
              }));
            }}
          />
          <span className="ml-2 font-bold">Assign Teams Pro License</span>
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label={
            <span>
              Phone Number
              <Tooltip
                placement="right"
                title={
                  <p>
                    Check 'Assign Teams Pro License' to enable Phone Number
                    selection
                  </p>
                }
                color="#333333cc"
              >
                <InfoCircleOutlined className="ml-1 mr-1" />
              </Tooltip>
            </span>
          }
          rules={[
            {
              required: assignTeamsProLicensesChecked,
              message: "This field is required",
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Select a phone number"
            onChange={() => {}}
            allowClear
            options={getLabelsAsValues(sipNumberOptions)}
            filterOption={(inputValue, option) =>
              option!.value.toUpperCase().startsWith(inputValue.toUpperCase())
            }
            disabled={!assignTeamsProLicensesChecked}
          ></Select>
        </Form.Item>

        <Form.Item
          label="Meeting Room Capacity"
          name="meetingRoomCapacity"
          rules={[
            {
              required: true,
              message: "This field is required",
            },
            {
              type: "number",
              min: 1,
              max: 100,
              message: "Capacity must be a number between 1 and 100",
            },
          ]}
        >
          <InputNumber
            className="w-full"
            placeholder="Enter a number between 1 and 100"
          />
        </Form.Item>
      </Form>

      {submitError && <div className="text-red-500">{submitError}</div>}
    </Modal>
  );
};

export default AddMeetingRoomFormModal;
